/**
 * Parser for the constraint-rule syntax — the inverse of the pretty-printer in
 * ruleAst.ts. `parseRule(ruleToString(r))` round-trips back to `r`, and
 * `parseObjective(objectiveToString(o))` back to `o`.
 *
 * This is what makes rules *editable*: the UI keeps each rule as text, and the
 * text is parsed back into the AST the interpreter grounds.
 *
 * ## A rule is a function definition
 *
 * The quantifiers are the parameters, each annotated with the relation its row
 * variable ranges over, and the body is the assertion that holds for every
 * combination of them:
 *
 *   def dcpf(l: line, t: timestep):
 *     P_line(l, t) == (theta(l.from, t) - theta(l.to, t)) / l.reactance
 *
 * A parameter can carry a filter, the way a comprehension does — `n: bus if
 * n.in_balance == 1` — and a `sum` *is* a generator expression:
 * `sum(P_line(l, t) for l in line if l.to == n)`. A cost category is a
 * definition with no parameters, whose body is a bare expression.
 *
 * Expressions are conditional the same way Python's are — `x if p else y`, with
 * `else` chaining — so a value defined by cases stays one definition instead of
 * one rule per case. When the conditional *is* the whole body, it's written as
 * an if/elif/else block instead, which is the same {@link Cond} laid out with
 * the tests down the left margin and the values indented under them.
 *
 * `if` is therefore three things at once: a parameter's filter, a comprehension
 * clause's filter, and a conditional. They don't collide, because a predicate's
 * operands are parsed as arithmetic rather than as full expressions — so the
 * `if` that ends a test always belongs to the construct the test is inside, and
 * a conditional only starts where a whole value is expected.
 *
 * ## Grammar
 *
 *   rule       := 'def' ident '(' (param (',' param)*)? ')' ':' body
 *   param      := ident ':' ident ('if' pred)?
 *   body       := expr relop expr                 -- a comparison row
 *               | expr '<=' expr '<=' expr        -- a ranged (bounds) rule
 *               | value                           -- a derived data relation
 *   value      := block | expr
 *   block      := 'if' pred ':' expr ('elif' pred ':' expr)* 'else' ':' expr
 *   cost       := 'def' ident '(' ')' ':' value   -- one summand of the objective
 *   objective  := ('minimize' | 'maximize') ident ':' expr
 *   pred       := compare ('and' compare)*
 *   compare    := arith ('=='|'!='|'<'|'<='|'>'|'>=') arith
 *   expr       := arith ('if' pred 'else' expr)?  -- a conditional; else chains
 *   arith      := term (('+'|'-') term)*
 *   term       := unary (('*'|'/') unary)*
 *   unary      := '-' unary | power
 *   power      := atom ('^' unary)?        // right-associative
 *   atom       := number | string
 *               | '(' expr ')'
 *               | 'sum' '(' expr clause+ ')'      -- a generator expression
 *               | ident '(' arg (',' arg)* ')'    -- a builtin call, else an applied relation
 *               | ident '.' ident                 -- an attribute read
 *               | ident                           -- a bound row variable
 *   clause     := 'for' ident 'in' ident ('if' pred)?
 *   arg        := ident '=' expr | expr           -- keyword or positional
 *
 * Newlines are whitespace: the body's indentation is a convention of the
 * printer, not something the grammar counts. A definition holds one statement,
 * so there's nothing for an indentation stack to disambiguate.
 *
 * A rule names itself, and so does a cost category: the name in the `def` head
 * is the constraint group its grounded rows carry, so renaming one is an
 * ordinary edit rather than a separate field beside the text.
 *
 * A ranged body may also be written `upper >= expr >= lower`; it parses to the
 * same {@link Range} the `<=` form does (and prints back in the `<=` form).
 */

import {
  Expr,
  Arg,
  Pred,
  Rule,
  Objective,
  Quantifier,
  ConstraintExpr,
  CompareOp,
  BUILTIN_FUNCTIONS,
  num,
  str,
  ref,
  attr,
  apply,
  call,
  neg,
  sum,
  compare,
  and,
  cmp,
  cond,
  range,
  define,
  RuleBody,
} from './ruleAst';
import { Span, Spans } from './diagnostics';

/** A syntax error, carrying the 1-based position of the offending token. */
export class ParseError extends Error {
  readonly line: number;
  readonly column: number;
  /** The offending token's extent, so an editor can underline it. */
  readonly span: Span;

  constructor(message: string, line: number, column: number, end?: { line: number; column: number }) {
    super(`line ${line}, column ${column}: ${message}`);
    this.name = 'ParseError';
    this.line = line;
    this.column = column;
    this.span = {
      startLine: line,
      startColumn: column,
      endLine: end?.line ?? line,
      endColumn: end?.column ?? column + 1,
    };
    // Restore the prototype chain: subclassing Error is lossy when TypeScript
    // downlevels the class, and `instanceof ParseError` has to keep working.
    Object.setPrototypeOf(this, ParseError.prototype);
  }
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

export type TokenKind = 'num' | 'str' | 'ident' | 'punct' | 'eof';

export interface Token {
  kind: TokenKind;
  text: string;
  line: number;
  column: number;
  /** One past the last character, so a token's extent is [column, endColumn). */
  endLine: number;
  endColumn: number;
}

/** Longest match first, so `<=` never tokenizes as `<` then `=`. */
const PUNCTUATION = ['==', '!=', '<=', '>=', '<', '>', '=', '+', '-', '*', '/', '^', '(', ')', ',', ':', '.'];

const IDENT_START = /[A-Za-z_]/;
const IDENT_REST = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;

/**
 * Split source into tokens.
 *
 * `lenient` is for the editor rather than the compiler: half-typed text is
 * *expected* to hold an unterminated string or a stray character, and the
 * services that read tokens to offer completions shouldn't go blind for the
 * keystroke it takes to close a quote. Leniently, a bad character is skipped and
 * an unterminated string ends at the line's end; strictly, either throws.
 */
export function tokenize(src: string, lenient = false): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  let line = 1;
  let lineStart = 0;
  const column = () => i - lineStart + 1;
  const push = (kind: TokenKind, text: string, start: { line: number; col: number }) =>
    tokens.push({ kind, text, line: start.line, column: start.col, endLine: line, endColumn: column() });

  while (i < src.length) {
    const ch = src[i];

    if (ch === '\n') {
      i += 1;
      line += 1;
      lineStart = i;
      continue;
    }
    if (ch === ' ' || ch === '\t' || ch === '\r') {
      i += 1;
      continue;
    }

    const start = { line, col: column() };

    if (ch === '"' || ch === "'") {
      const quote = ch;
      i += 1;
      let text = '';
      while (i < src.length && src[i] !== quote && src[i] !== '\n') {
        // Backslash escapes the next character, so a quote can sit in a string.
        if (src[i] === '\\' && i + 1 < src.length) i += 1;
        text += src[i++];
      }
      if (src[i] !== quote) {
        if (!lenient) throw new ParseError('unterminated string', start.line, start.col);
      } else {
        i += 1; // closing quote
      }
      push('str', text, start);
      continue;
    }

    if (DIGIT.test(ch)) {
      let text = '';
      while (i < src.length && DIGIT.test(src[i])) text += src[i++];
      if (src[i] === '.' && DIGIT.test(src[i + 1] ?? '')) {
        text += src[i++];
        while (i < src.length && DIGIT.test(src[i])) text += src[i++];
      }
      push('num', text, start);
      continue;
    }

    if (IDENT_START.test(ch)) {
      let text = '';
      while (i < src.length && IDENT_REST.test(src[i])) text += src[i++];
      push('ident', text, start);
      continue;
    }

    const punct = PUNCTUATION.find((p) => src.startsWith(p, i));
    if (punct) {
      i += punct.length;
      push('punct', punct, start);
      continue;
    }

    if (!lenient) throw new ParseError(`unexpected character '${ch}'`, start.line, start.col);
    i += 1;
  }

  tokens.push({
    kind: 'eof',
    text: '<end of input>',
    line,
    column: column(),
    endLine: line,
    endColumn: column() + 1,
  });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

const COMPARE_OPS: CompareOp[] = ['==', '!=', '<=', '>=', '<', '>'];

class Parser {
  private pos = 0;
  /**
   * Where each node came from, keyed by identity. Filled as the parse returns:
   * every method that builds a node marks it with the text it consumed.
   */
  readonly spans: Spans = new Map();
  /** The extent of the name in the `def` head — what a name clash is about. */
  nameSpan?: Span;

  constructor(private readonly tokens: Token[]) {}

  // --- span helpers --------------------------------------------------------

  /** Record `node` as spanning from `start` through the last token consumed. */
  private mark<T>(start: Token, node: T): T {
    const end = this.pos > 0 ? this.tokens[this.pos - 1] : start;
    this.spans.set(node, {
      startLine: start.line,
      startColumn: start.column,
      endLine: end.endLine,
      endColumn: end.endColumn,
    });
    return node;
  }

  /**
   * Run `parse`, recording the text it consumed as the span of what it returns.
   *
   * Every level of the expression grammar is wrapped, so a position resolves to
   * the innermost node that covers it — hovering `l.from` inside `theta(l.from,
   * t) / l.reactance` asks about the attribute, not the division. A level that
   * passes its operand through unchanged (an `arith` that turned out to be a
   * bare `term`) re-marks the same node with the same extent.
   */
  private spanning<T>(parse: () => T): T {
    const start = this.peek();
    return this.mark(start, parse());
  }

  // --- token helpers -------------------------------------------------------

  private peek(offset = 0): Token {
    return this.tokens[Math.min(this.pos + offset, this.tokens.length - 1)];
  }

  private next(): Token {
    const token = this.peek();
    if (token.kind !== 'eof') this.pos += 1;
    return token;
  }

  private fail(message: string, token: Token = this.peek()): never {
    // The whole offending token, so the editor underlines the word that's wrong
    // rather than the character it starts with. End-of-input has no width, so it
    // falls back to the one-column marker the constructor supplies.
    const end = token.kind === 'eof' ? undefined : { line: token.endLine, column: token.endColumn };
    throw new ParseError(message, token.line, token.column, end);
  }

  private atPunct(text: string, offset = 0): boolean {
    const token = this.peek(offset);
    return token.kind === 'punct' && token.text === text;
  }

  private atKeyword(word: string, offset = 0): boolean {
    const token = this.peek(offset);
    return token.kind === 'ident' && token.text === word;
  }

  private matchPunct(text: string): boolean {
    if (!this.atPunct(text)) return false;
    this.next();
    return true;
  }

  private expectPunct(text: string): Token {
    if (!this.atPunct(text)) this.fail(`expected '${text}', got '${this.peek().text}'`);
    return this.next();
  }

  private expectKeyword(word: string): Token {
    if (!this.atKeyword(word)) this.fail(`expected '${word}', got '${this.peek().text}'`);
    return this.next();
  }

  private expectIdent(what: string): string {
    const token = this.peek();
    if (token.kind !== 'ident') this.fail(`expected ${what}, got '${token.text}'`);
    return this.next().text;
  }

  private expectEnd(): void {
    if (this.peek().kind !== 'eof') this.fail(`unexpected '${this.peek().text}' after the end of the rule`);
  }

  // --- top level -----------------------------------------------------------

  parseRule(): Rule {
    const name = this.parseDefHead('a rule name');
    const quantifiers: Quantifier[] = [];
    if (!this.atPunct(')')) {
      do {
        quantifiers.push(this.parseParam());
      } while (this.matchPunct(','));
    }
    this.expectPunct(')');
    this.expectPunct(':');
    const body = this.parseConstraintBody();
    this.expectEnd();
    return { name, quantifiers, body };
  }

  /** `def <name>(` — shared by rules and cost categories. Leaves the params next. */
  private parseDefHead(what: string): string {
    this.expectKeyword('def');
    const nameToken = this.peek();
    const name = this.expectIdent(what);
    this.nameSpan = {
      startLine: nameToken.line,
      startColumn: nameToken.column,
      endLine: nameToken.endLine,
      endColumn: nameToken.endColumn,
    };
    this.expectPunct('(');
    return name;
  }

  /** `<var>: <relation>`, optionally filtered — one parameter of a rule. */
  private parseParam(): Quantifier {
    return this.spanning(() => this.parseParamNode());
  }

  private parseParamNode(): Quantifier {
    const variable = this.expectIdent('a variable name');
    this.expectPunct(':');
    const relation = this.expectIdent('a relation name');
    if (!this.atKeyword('if')) return { var: variable, relation };
    this.next();
    return { var: variable, relation, where: this.parsePred() };
  }

  /** `def <name>(): <expr>` — one named summand of the objective. */
  parseCostTerm(): { name: string; expr: Expr } {
    const name = this.parseDefHead('a cost category name');
    // A cost category quantifies nothing of its own; its sums bring their own.
    if (!this.atPunct(')')) this.fail('a cost category takes no parameters');
    this.expectPunct(')');
    this.expectPunct(':');
    const expr = this.atKeyword('if') ? this.parseBlockCond() : this.parseExpr();
    this.expectEnd();
    return { name, expr };
  }

  parseObjective(): Objective {
    const senseToken = this.peek();
    if (!this.atKeyword('minimize') && !this.atKeyword('maximize')) {
      this.fail(`expected 'minimize' or 'maximize', got '${senseToken.text}'`);
    }
    const sense = this.next().text as Objective['sense'];
    const name = this.expectIdent('an objective name');
    this.expectPunct(':');
    const expr = this.parseExpr();
    this.expectEnd();
    return { sense, name, expr };
  }

  /**
   * `left <op> right`, or the two-sided `lower <= expr <= upper` that declares a
   * variable's bounds. `==` is accepted as a spelling of the equality `=`.
   */
  private parseConstraintBody(): RuleBody {
    // A body that opens with `if` is a block of cases, and so a value: nothing
    // is being asserted, the rule defines a relation.
    if (this.atKeyword('if')) return define(this.parseBlockCond());
    const left = this.parseExpr();
    // No relational operator: the body is a value, so the rule defines a
    // derived relation rather than asserting anything about one.
    if (this.peek().kind === 'eof') return define(left);
    const opToken = this.peek();
    const op = this.takeBodyOp();
    const middle = this.parseExpr();

    if ((op === '<=' || op === '>=') && (this.atPunct('<=') || this.atPunct('>='))) {
      const secondToken = this.peek();
      const second = this.takeBodyOp();
      if (second !== op) {
        this.fail(`chained comparison must use the same direction ('${op}' then '${second}')`, secondToken);
      }
      const right = this.parseExpr();
      // `a >= b >= c` is the same range as `c <= b <= a`.
      return op === '<=' ? range(left, middle, right) : range(right, middle, left);
    }

    if (op === '<' || op === '>') {
      this.fail(`'${op}' is not a constraint operator; use '<=', '>=' or '=='`, opToken);
    }
    return cmp(op === '==' ? '=' : (op as '<=' | '>=' | '='), left, middle);
  }

  /** Consume the relational operator of a constraint body. */
  private takeBodyOp(): string {
    const token = this.peek();
    if (token.kind !== 'punct' || !['<=', '>=', '=', '==', '<', '>'].includes(token.text)) {
      this.fail(`expected '<=', '>=' or '==', got '${token.text}'`);
    }
    return this.next().text;
  }

  // --- quantifiers + predicates -------------------------------------------

  /** `for <var> in <relation>` with an optional `if` filter — one comprehension clause. */
  private parseClause(): Quantifier {
    return this.spanning(() => this.parseClauseNode());
  }

  private parseClauseNode(): Quantifier {
    this.expectKeyword('for');
    const variable = this.expectIdent('a variable name');
    this.expectKeyword('in');
    const relation = this.expectIdent('a relation name');
    if (!this.atKeyword('if')) return { var: variable, relation };
    this.next();
    return { var: variable, relation, where: this.parsePred() };
  }

  private parsePred(): Pred {
    return this.spanning(() => this.parsePredNode());
  }

  private parsePredNode(): Pred {
    let left: Pred = this.parseCompare();
    while (this.atKeyword('and')) {
      this.next();
      left = and(left, this.parseCompare());
    }
    return left;
  }

  private parseCompare(): Pred {
    return this.spanning(() => this.parseCompareNode());
  }

  private parseCompareNode(): Pred {
    // Arithmetic, not a full expression: a test's operands stop short of `if`,
    // which here belongs to whatever the predicate is filtering. A conditional
    // can still be compared by parenthesizing it.
    const left = this.parseArith();
    const token = this.peek();
    if (token.kind !== 'punct' || !COMPARE_OPS.includes(token.text as CompareOp)) {
      const hint = token.text === '=' ? " (use '==' to compare)" : '';
      this.fail(`expected a comparison operator, got '${token.text}'${hint}`);
    }
    this.next();
    return compare(token.text as CompareOp, left, this.parseArith());
  }

  // --- expressions ---------------------------------------------------------

  /**
   * A chain of cases as a block, when the conditional *is* the whole body:
   *
   *   if g.fuel == "wind":
   *     min(g.max, wind_strength(t))
   *   elif g.fuel == "solar":
   *     min(g.max, solar_strength(t))
   *   else:
   *     g.max
   *
   * Builds the same {@link Cond} as the ternary — `elif` is the `else` branch
   * being another conditional. The `else` is required: a conditional is a value,
   * and every case has to produce one.
   *
   * The indentation is a convention of the printer, not something the grammar
   * counts: a branch ends where the next `elif`/`else` begins, which is
   * unambiguous because neither can start an expression.
   */
  private parseBlockCond(head: 'if' | 'elif' = 'if'): Expr {
    return this.spanning(() => this.parseBlockCondNode(head));
  }

  private parseBlockCondNode(head: 'if' | 'elif'): Expr {
    this.expectKeyword(head);
    const when = this.parsePred();
    this.expectPunct(':');
    const then = this.parseExpr();
    if (this.atKeyword('elif')) return cond(when, then, this.parseBlockCond('elif'));
    if (!this.atKeyword('else')) this.fail(`expected 'elif' or 'else', got '${this.peek().text}'`);
    this.next();
    this.expectPunct(':');
    return cond(when, then, this.parseExpr());
  }

  /**
   * `<value> if <pred> else <value>`, or plain arithmetic when there's no `if`.
   * Right-associative, so the `else` branch takes another conditional and a
   * chain of cases needs no parentheses.
   */
  private parseExpr(): Expr {
    return this.spanning(() => this.parseExprNode());
  }

  private parseExprNode(): Expr {
    const value = this.parseArith();
    if (!this.atKeyword('if')) return value;
    this.next();
    const when = this.parsePred();
    this.expectKeyword('else');
    return cond(when, value, this.parseExpr());
  }

  private parseArith(): Expr {
    return this.spanning(() => this.parseArithNode());
  }

  private parseArithNode(): Expr {
    let left = this.parseTerm();
    while (this.atPunct('+') || this.atPunct('-')) {
      const op = this.next().text as '+' | '-';
      left = { kind: 'binary', op, left, right: this.parseTerm() };
    }
    return left;
  }

  private parseTerm(): Expr {
    return this.spanning(() => this.parseTermNode());
  }

  private parseTermNode(): Expr {
    let left = this.parseUnary();
    while (this.atPunct('*') || this.atPunct('/')) {
      const op = this.next().text as '*' | '/';
      left = { kind: 'binary', op, left, right: this.parseUnary() };
    }
    return left;
  }

  private parseUnary(): Expr {
    return this.spanning(() => this.parseUnaryNode());
  }

  private parseUnaryNode(): Expr {
    if (this.matchPunct('-')) return neg(this.parseUnary());
    return this.parsePower();
  }

  /**
   * `^` binds tighter than unary minus and than `*`/`/`, and is right-associative
   * (`2^3^2` = `2^(3^2)`, `-x^2` = `-(x^2)`). Its exponent is a unary so
   * `x^-1` parses; its base is an atom.
   */
  private parsePower(): Expr {
    const base = this.parseAtom();
    if (this.matchPunct('^')) {
      return { kind: 'binary', op: '^', left: base, right: this.parseUnary() };
    }
    return base;
  }

  /**
   * Atoms are what validation reports on — a relation applied, an attribute
   * read, a bare row variable — so they're the nodes worth a span. Compound
   * expressions are never a message's subject: a message about `a + b` is
   * really about one of its atoms.
   */
  private parseAtom(): Expr {
    return this.spanning(() => this.parseAtomNode());
  }

  private parseAtomNode(): Expr {
    const token = this.peek();

    if (token.kind === 'num') {
      this.next();
      return num(Number(token.text));
    }

    if (token.kind === 'str') {
      this.next();
      return str(token.text);
    }

    if (this.atPunct('(')) {
      this.next();
      const inner = this.parseExpr();
      this.expectPunct(')');
      return inner;
    }

    if (token.kind !== 'ident') this.fail(`expected an expression, got '${token.text}'`);

    if (token.text === 'sum' && this.atPunct('(', 1)) return this.parseSum();

    const name = this.next().text;

    if (this.atPunct('(')) {
      const args = this.parseArgs();
      if (BUILTIN_FUNCTIONS.has(name)) {
        const keyword = args.find((a) => a.name);
        if (keyword) this.fail(`'${name}' is a builtin function and takes no keyword arguments`, token);
        return call(name, ...args.map((a) => a.value));
      }
      return apply(name, ...args);
    }

    if (this.matchPunct('.')) return attr(name, this.expectIdent('an attribute name'));

    return ref(name);
  }

  private parseSum(): Expr {
    return this.spanning(() => this.parseSumNode());
  }

  private parseSumNode(): Expr {
    this.expectKeyword('sum');
    this.expectPunct('(');
    const body = this.parseExpr();
    if (!this.atKeyword('for')) this.fail("expected 'for' — a sum ranges over something");
    const over: Quantifier[] = [];
    while (this.atKeyword('for')) over.push(this.parseClause());
    this.expectPunct(')');
    return sum(over, body);
  }

  private parseArgs(): Arg[] {
    this.expectPunct('(');
    const args: Arg[] = [];
    if (!this.atPunct(')')) {
      do {
        args.push(this.parseArg());
      } while (this.matchPunct(','));
    }
    this.expectPunct(')');
    return args;
  }

  /** `name=expr` when an identifier is followed by a bare `=`, else positional. */
  private parseArg(): Arg {
    const token = this.peek();
    if (token.kind === 'ident' && this.atPunct('=', 1)) {
      this.next();
      this.next();
      return { name: token.text, value: this.parseExpr() };
    }
    return { value: this.parseExpr() };
  }
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

/**
 * A parse together with where its pieces came from — what an editor needs to
 * underline the node a message is about. The AST is the same object model
 * {@link parseRule} returns; the positions ride alongside in {@link Spans}.
 */
export interface ParsedRule {
  rule: Rule;
  spans: Spans;
  /** The name in the `def` head — where a name clash gets reported. */
  nameSpan?: Span;
}

/**
 * Parse a rule (`def <name>(<params>): <body>`) into a {@link Rule}.
 * Throws {@link ParseError} on a syntax error.
 */
export function parseRule(source: string): Rule {
  return new Parser(tokenize(source)).parseRule();
}

/** {@link parseRule}, keeping the source positions of what it parsed. */
export function parseRuleWithSpans(source: string): ParsedRule {
  const parser = new Parser(tokenize(source));
  const rule = parser.parseRule();
  return { rule, spans: parser.spans, nameSpan: parser.nameSpan };
}

/** {@link parseCostTerm}, keeping the source positions of what it parsed. */
export function parseCostTermWithSpans(
  source: string,
): { term: { name: string; expr: Expr }; spans: Spans; nameSpan?: Span } {
  const parser = new Parser(tokenize(source));
  const term = parser.parseCostTerm();
  return { term, spans: parser.spans, nameSpan: parser.nameSpan };
}

/** Parse `minimize <name> = <expr>` into an {@link Objective}. */
export function parseObjective(source: string): Objective {
  return new Parser(tokenize(source)).parseObjective();
}

/**
 * Parse a cost category (`def <name>(): <expr>`) — one named summand of the
 * objective, which carries no parameters and no `minimize` head of its own.
 */
export function parseCostTerm(source: string): { name: string; expr: Expr } {
  return new Parser(tokenize(source)).parseCostTerm();
}
